/**
 * ONE derivation of "how much capital is withheld by an active harvest lockout", shared by
 * every surface that shows the CIO a number he could read as available to deploy.
 *
 * WHY THIS EXISTS.
 * The engine subtracts locked harvest capital from the buy budget in exactly one place —
 * strategy/rebalance_logic.py:947-950:
 *
 *     home_run_locked_notional = float(home_run_redeployment.get("lockedNotional") or 0.0)
 *     effective_buy_headroom = max(target_pct_effective * portfolio_nav
 *                                  - leaps_nav - home_run_locked_notional, 0.0)
 *
 * and nothing downstream showed it. The Risk cash-posture tile, the Screener sizing budget
 * and the Signals deployment track all rendered capital as available while a lockout was
 * withholding it. Worse, when a lock merely SHRANK the budget instead of zeroing it, no
 * screen said so at all. Three separate frontend calculations would have re-created that
 * disagreement, so the arithmetic and the wording both live here.
 *
 * ---------------------------------------------------------------------------------------
 * THE TWO NOTIONS OF "LOCKED" — AND WHICH LABEL USES WHICH.
 *
 * strategy/home_run_redeployment.py:get_locked_redeployment_summary() ships BOTH:
 *
 *     "lockedNotional":    locked if policy.enabled and policy.apply_to_buy_budget else 0.0
 *     "rawLockedNotional": locked                       # the raw sum of ACTIVE locks
 *
 * They differ whenever the policy is off. So:
 *
 *   rawLockedNotional      -> the label "under lockout". Capital the CIO should not plan to
 *                             redeploy, whether or not the engine polices it.
 *   withheldFromBuyBudget  -> the label "withheld from the buy budget". The ONLY figure that
 *                             may be subtracted from anything presented as deployable,
 *                             because it is the only one the engine itself subtracts.
 *
 * A screen therefore never says "your budget is reduced by $X" while the engine is not
 * reducing it: when apply_to_buy_budget is off we report status "locked_not_applied" and say
 * plainly that the deployable figure is NOT reduced.
 *
 * ---------------------------------------------------------------------------------------
 * HOUSE RULE: NEVER RENDER A REASSURING VALUE WE CANNOT SUBSTANTIATE.
 *
 * Both backend producers collapse to a confident-looking zero on failure:
 *
 *   - get_locked_redeployment_summary() catches, and returns
 *     {enabled: False, lockedNotional: 0.0, ..., "error": str(exc)}.
 *   - rebalance_logic's local default (used when the import itself failed) is
 *     {enabled: False, lockedNotional: 0.0, activeLockCount: 0, ...} with NO
 *     applyToBuyBudget and NO rawLockedNotional key.
 *   - portfolio_view falls back to {policy: {}, summary: {lockedNotional: 0.0, ...}} and
 *     appends "home_run:<exc>" to payload.warnings.
 *
 * "$0 locked" from any of those is a guess, not a reading. We only trust a source that
 * proves the real code path ran:
 *
 *   engine  -> home_run_redeployment carries a BOOLEAN applyToBuyBudget (only
 *              get_locked_redeployment_summary emits that key) and carries no `error`.
 *   harvest -> policy.enabled and policy.apply_to_buy_budget are BOTH booleans (only
 *              load_home_run_policy() coerces them to bool; the exception fallback leaves
 *              policy as {}), and summary.lockedNotional is a number.
 *
 * Anything else, including a payload that is simply too old, is status "unknown" — and the
 * caller must print an em dash and a warning, never a number. This mirrors the guard the
 * Signals harvest card already uses ("Harvest data unavailable" / `typeof policy?.enabled
 * !== "boolean"` -> "—"); it is deliberately the same test, not a second one.
 *
 * ---------------------------------------------------------------------------------------
 * DISPLAY ONLY. Nothing here blocks, disables or gates an action. The lockout is advisory:
 * this module changes what the CIO SEES, never what he is permitted to do.
 */

/** Default age past which a harvest reading stops being evidence. Locks are created by hand
 *  and unlock dates are days out, so this is generous; the point is that a page left open
 *  overnight must not keep asserting "no lockout". */
export const HARVEST_LOCKOUT_MAX_AGE_MS = 30 * 60 * 1000;

/** GET /home_run_harvest, and get_portfolio_view's `homeRun` block (same builder). */
export type HarvestPayloadLike = {
  policy?: unknown;
  summary?: unknown;
  activeLocks?: unknown;
  error?: unknown;
} | null | undefined;

/** The rebalance meta the engine emits (get_portfolio_view `rebalance`; the flat engine
 *  meta in get_leaps_snapshot?includeEngine=1). */
export type EngineRebalanceMetaLike = {
  home_run_redeployment?: unknown;
  home_run_locked_notional?: unknown;
  effective_buy_headroom_after_home_run_lock?: unknown;
  buy_block_reason?: unknown;
} | null | undefined;

export type HarvestLockoutStatus =
  /** Cannot be substantiated. Print "—", never a number. */
  | "unknown"
  /** Policy read cleanly and no capital is under lock. */
  | "none"
  /** Capital is under lock AND the engine subtracts it from the buy budget. */
  | "withheld"
  /** Capital is under lock but policy.apply_to_buy_budget is off, so the engine does not
   *  subtract it. Deployable figures are NOT reduced. */
  | "locked_not_applied";

export type HarvestLockout = {
  status: HarvestLockoutStatus;
  known: boolean;
  /** Plain reason the reading is unusable; null when known. */
  unknownReason: string | null;
  /** Which producer the figures came from. */
  source: "engine" | "harvest" | "none";
  /** Raw sum of ACTIVE locks — "under lockout". */
  rawLockedNotional: number | null;
  /** What the engine actually subtracts from the buy budget. */
  withheldFromBuyBudget: number | null;
  lockedPctNav: number | null;
  activeLockCount: number | null;
  nextUnlockAtUTC: string | null;
  /** Whole days to the earliest unlock, from the backend's own DATEDIFF when available. */
  daysUntilNextUnlock: number | null;
  policyEnabled: boolean | null;
  applyToBuyBudget: boolean | null;
  /** The engine's OWN sentence, verbatim from `buy_block_reason`, never re-worded here.
   *  Present only when the lock actually zeroed the buy headroom. */
  engineBuyBlockReason: string | null;
  /** The engine's own post-lock headroom, when the payload carried it. */
  engineEffectiveBuyHeadroom: number | null;
  stale: boolean;
  /** Short label for a pill or tile heading. */
  headline: string;
  /** Full disclosure sentence(s). Ends with the engine's verbatim sentence when there is one. */
  detail: string;
};

export type DeployableSuppression = "unknown" | "none" | "partial" | "full" | "not_applied";

export type DeployableFigure = {
  /** The figure as the page computed it, before the lockout. */
  gross: number | null;
  /** What is genuinely available. null whenever it cannot be substantiated. */
  net: number | null;
  /** True when `net` must be rendered as "—" rather than a number. */
  unknown: boolean;
  suppression: DeployableSuppression;
  /** What to print beside the figure. Never empty. */
  disclosure: string;
};

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const bool = (v: unknown): boolean | null => (typeof v === "boolean" ? v : null);

const int = (v: unknown): number | null => {
  const n = num(v);
  return n === null ? null : Math.trunc(n);
};

const str = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
};

/** USD, whole dollars. Deliberately local and deterministic so every surface prints the
 *  same string and the tests do not depend on a locale. */
export function fmtLockUsd(v: number | null): string {
  if (v === null) return "—";
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
}

/** UTC calendar date. An unlock is a date, not a moment, and a UTC date cannot drift with
 *  the reader's timezone the way a formatted local timestamp can. */
export function fmtUnlockDate(iso: string | null): string {
  if (!iso) return "an unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "an unknown date";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day} UTC`;
}

type ReadResult = {
  rawLockedNotional: number;
  withheldFromBuyBudget: number;
  lockedPctNav: number | null;
  activeLockCount: number | null;
  nextUnlockAtUTC: string | null;
  daysUntilNextUnlock: number | null;
  policyEnabled: boolean;
  applyToBuyBudget: boolean;
};

/**
 * Read the engine's own lock summary out of rebalance meta.
 *
 * Trusted ONLY when `applyToBuyBudget` is a boolean and no `error` is present — see the
 * file header. rebalance_logic's import-failure default carries neither
 * `applyToBuyBudget` nor `rawLockedNotional`, which is exactly what makes it detectable.
 */
function readEngine(engine: EngineRebalanceMetaLike): ReadResult | null {
  if (!engine || typeof engine !== "object") return null;
  const hr = (engine as Record<string, unknown>).home_run_redeployment as Record<string, unknown> | null | undefined;
  if (!hr || typeof hr !== "object") return null;
  if (hr.error !== undefined && hr.error !== null) return null;

  const applyToBuyBudget = bool(hr.applyToBuyBudget);
  const enabled = bool(hr.enabled);
  if (applyToBuyBudget === null || enabled === null) return null;

  // rawLockedNotional is the sum of ACTIVE locks regardless of policy; lockedNotional is
  // already policy-gated by the backend. Prefer the engine's own gated figure, which is
  // the literal input to effective_buy_headroom.
  const raw = num(hr.rawLockedNotional);
  const gated = num(hr.lockedNotional);
  if (raw === null || gated === null) return null;

  return {
    rawLockedNotional: raw,
    withheldFromBuyBudget: gated,
    lockedPctNav: num(hr.lockedPctNav),
    activeLockCount: int(hr.activeLockCount),
    nextUnlockAtUTC: str(hr.nextUnlockAtUTC),
    daysUntilNextUnlock: null, // the summary carries no per-lock rows
    policyEnabled: enabled,
    applyToBuyBudget,
  };
}

/**
 * Read GET /home_run_harvest (or get_portfolio_view.homeRun).
 *
 * `summary.lockedNotional` here is the RAW sum — build_home_run_dashboard_payload does NOT
 * apply the policy gate that get_locked_redeployment_summary applies. So the gate is
 * re-applied here with the same expression the backend uses:
 *     locked if policy.enabled and policy.apply_to_buy_budget else 0.0
 */
function readHarvest(harvest: HarvestPayloadLike): ReadResult | null {
  if (!harvest || typeof harvest !== "object") return null;
  if (harvest.error !== undefined && harvest.error !== null) return null;

  const policy = harvest.policy as Record<string, unknown> | null | undefined;
  const summary = harvest.summary as Record<string, unknown> | null | undefined;
  if (!policy || typeof policy !== "object") return null;
  if (!summary || typeof summary !== "object") return null;

  const enabled = bool(policy.enabled);
  const applyToBuyBudget = bool(policy.apply_to_buy_budget);
  if (enabled === null || applyToBuyBudget === null) return null;

  const raw = num(summary.lockedNotional);
  if (raw === null) return null;

  let daysUntilNextUnlock: number | null = null;
  const locks = harvest.activeLocks;
  if (Array.isArray(locks)) {
    for (const lock of locks) {
      if (!lock || typeof lock !== "object") continue;
      const d = int((lock as Record<string, unknown>).daysUntilUnlock);
      if (d === null) continue;
      daysUntilNextUnlock = daysUntilNextUnlock === null ? d : Math.min(daysUntilNextUnlock, d);
    }
  }

  return {
    rawLockedNotional: raw,
    withheldFromBuyBudget: enabled && applyToBuyBudget ? raw : 0,
    lockedPctNav: num(summary.lockedPctNav),
    activeLockCount: int(summary.activeLockCount),
    nextUnlockAtUTC: str(summary.nextUnlockAtUTC),
    daysUntilNextUnlock,
    policyEnabled: enabled,
    applyToBuyBudget,
  };
}

function unknown(reason: string, stale = false): HarvestLockout {
  return {
    status: "unknown",
    known: false,
    unknownReason: reason,
    source: "none",
    rawLockedNotional: null,
    withheldFromBuyBudget: null,
    lockedPctNav: null,
    activeLockCount: null,
    nextUnlockAtUTC: null,
    daysUntilNextUnlock: null,
    policyEnabled: null,
    applyToBuyBudget: null,
    engineBuyBlockReason: null,
    engineEffectiveBuyHeadroom: null,
    stale,
    headline: "Harvest lockout unknown",
    detail:
      `Harvest lockout data unavailable (${reason}). Capital withheld by a lockout cannot be ` +
      `accounted for, so any figure shown as available to deploy is UNCONFIRMED — check the ` +
      `Signals harvest panel before deploying.`,
  };
}

export function deriveHarvestLockout(input: {
  /** GET /home_run_harvest, or get_portfolio_view.homeRun. */
  harvest?: HarvestPayloadLike;
  /** get_portfolio_view.rebalance, when the surface has it. Optional. */
  engine?: EngineRebalanceMetaLike;
  /** Date.now() when the payload above was received. Omit if the surface does not track it. */
  fetchedAtMs?: number | null;
  nowMs?: number;
  maxAgeMs?: number;
}): HarvestLockout {
  const fetchedAtMs = input.fetchedAtMs ?? null;
  const maxAgeMs = input.maxAgeMs ?? HARVEST_LOCKOUT_MAX_AGE_MS;
  const nowMs = input.nowMs ?? Date.now();

  if (fetchedAtMs !== null && Number.isFinite(fetchedAtMs) && nowMs - fetchedAtMs > maxAgeMs) {
    const mins = Math.round((nowMs - fetchedAtMs) / 60000);
    return unknown(`last read ${mins} min ago — too old to rely on`, true);
  }

  const fromEngine = readEngine(input.engine);
  const fromHarvest = readHarvest(input.harvest);
  const read = fromEngine ?? fromHarvest;

  if (!read) {
    const attempted = input.harvest != null || input.engine != null;
    return unknown(
      attempted
        ? "the lockout policy did not load, so a $0 reading cannot be trusted"
        : "no harvest payload was loaded"
    );
  }

  // Both producers read the same ACTIVE lock rows, so a real disagreement means one of the
  // two reads is not current. Refusing to pick a winner is the only option that cannot put
  // "capital is withheld" and "capital is free" on the same screen.
  if (fromEngine && fromHarvest && Math.abs(fromEngine.rawLockedNotional - fromHarvest.rawLockedNotional) > 1) {
    return unknown(
      `the rebalance engine reports ${fmtLockUsd(fromEngine.rawLockedNotional)} under lock and the ` +
        `harvest endpoint reports ${fmtLockUsd(fromHarvest.rawLockedNotional)} — the two disagree`
    );
  }

  // Per-lock detail only exists on the harvest payload, so borrow it even when the engine
  // supplied the money figures. Both describe the same ACTIVE lock set.
  const daysUntilNextUnlock = read.daysUntilNextUnlock ?? fromHarvest?.daysUntilNextUnlock ?? null;
  const nextUnlockAtUTC = read.nextUnlockAtUTC ?? fromHarvest?.nextUnlockAtUTC ?? null;
  const activeLockCount = read.activeLockCount ?? fromHarvest?.activeLockCount ?? null;
  const lockedPctNav = read.lockedPctNav ?? fromHarvest?.lockedPctNav ?? null;

  // The engine's OWN sentence, taken verbatim. rebalance_logic writes buy_block_reason for
  // three different causes and the lockout branch runs LAST (`if buy_block_reason is None
  // and home_run_locked_notional > 0 and effective_buy_headroom <= 0`), so a funded-cap or
  // growth-cap message can occupy the field while a lockout is also active. Matching the
  // lockout wording is how we avoid captioning a lockout with a cap message; it is a
  // selection, not a rewrite. When the lock only SHRANK the budget the engine writes no
  // sentence at all, which is why the partial disclosure below is assembled from figures.
  const engine = input.engine ?? undefined;
  const rawReason = engine ? str((engine as Record<string, unknown>).buy_block_reason) : null;
  const engineBuyBlockReason = rawReason && /redeployment lockout/i.test(rawReason) ? rawReason : null;
  const engineEffectiveBuyHeadroom = engine
    ? num((engine as Record<string, unknown>).effective_buy_headroom_after_home_run_lock)
    : null;

  const source: "engine" | "harvest" = fromEngine ? "engine" : "harvest";
  const raw = read.rawLockedNotional;
  const withheld = read.withheldFromBuyBudget;
  const locksLabel = activeLockCount === null ? "" : ` across ${activeLockCount} harvest${activeLockCount === 1 ? "" : "s"}`;
  const unlockLabel =
    nextUnlockAtUTC === null
      ? ""
      : ` Next unlock ${fmtUnlockDate(nextUnlockAtUTC)}${
          daysUntilNextUnlock === null ? "" : ` (${daysUntilNextUnlock} day${daysUntilNextUnlock === 1 ? "" : "s"})`
        }.`;

  const base = {
    known: true,
    unknownReason: null,
    source,
    rawLockedNotional: raw,
    withheldFromBuyBudget: withheld,
    lockedPctNav,
    activeLockCount,
    nextUnlockAtUTC,
    daysUntilNextUnlock,
    policyEnabled: read.policyEnabled,
    applyToBuyBudget: read.applyToBuyBudget,
    engineBuyBlockReason,
    engineEffectiveBuyHeadroom,
    stale: false,
  };

  if (raw <= 0) {
    return {
      ...base,
      status: "none",
      headline: "No harvest lockout",
      detail: "No harvested capital is under a redeployment lockout, so nothing is withheld from the buy budget.",
    };
  }

  if (withheld <= 0) {
    return {
      ...base,
      status: "locked_not_applied",
      headline: `${fmtLockUsd(raw)} locked — not withheld from the buy budget`,
      detail:
        `${fmtLockUsd(raw)} of harvested capital is under a redeployment lockout${locksLabel}, but the ` +
        `lockout policy ${read.policyEnabled ? "does not apply it to the buy budget" : "is disabled"}, ` +
        `so the engine does NOT subtract it. Figures shown as available to deploy are NOT reduced by it.` +
        unlockLabel,
    };
  }

  return {
    ...base,
    status: "withheld",
    headline: `${fmtLockUsd(withheld)} withheld by harvest lockout`,
    detail:
      `${fmtLockUsd(withheld)} of harvested capital is under a redeployment lockout${locksLabel} and is ` +
      `already subtracted from the engine's buy budget.` +
      unlockLabel +
      (engineBuyBlockReason ? ` Engine: "${engineBuyBlockReason}"` : ""),
  };
}

/**
 * The HEADLINE for a cash-posture / deployment-status tile.
 *
 * WHY THIS EXISTS. Risk and Signals both headlined "Fully Deployable" while the line
 * directly beneath, built from `netDeployable().disclosure`, said capital was withheld by
 * a lockout. The netted number was right; the headline contradicted it — and a headline is
 * what gets scanned. A tile whose big text says the opposite of its small text is worse
 * than one that never mentioned the lockout, because it looks like it has been checked.
 *
 * The two pages word their non-lockout states differently (Risk: "Cash Reserved" /
 * "Fully Deployable"; Signals: "Constrained by discretionary cash" / "Fully deployable"),
 * so those two strings are passed in. Everything the LOCKOUT contributes is decided here,
 * once, from the same derivation the disclosure is built from — which is the only way the
 * two lines cannot disagree again.
 */
export function deployabilityHeadline(
  lock: HarvestLockout,
  opts: {
    /** True when a discretionary cash reserve is also in force. */
    reserved: boolean;
    /** The page's own wording for the reserved-but-no-lockout state. */
    reservedLabel: string;
    /** The page's own wording for the nothing-constrains-it state. */
    freeLabel: string;
  }
): string {
  const { reserved, reservedLabel, freeLabel } = opts;

  // An unsubstantiated reading may never headline as "fully deployable": that is the
  // reassuring direction, and the whole module exists to refuse it.
  if (!lock.known) {
    return reserved ? `${reservedLabel} · lockout unknown` : "Deployability unconfirmed";
  }

  switch (lock.status) {
    case "withheld":
      return reserved ? `${reservedLabel} + lockout` : "Withheld by lockout";
    // Capital IS locked; the engine simply is not subtracting it. "Fully deployable" would
    // be true of the buy budget and false of the capital, so say both.
    case "locked_not_applied":
      return reserved ? `${reservedLabel} · under lockout` : "Under lockout, not withheld";
    case "none":
    default:
      return reserved ? reservedLabel : freeLabel;
  }
}

/**
 * Net a figure the CIO would read as available-to-deploy against the lockout.
 *
 * The contract the callers depend on: when `unknown` is true they MUST render "—" and the
 * disclosure, never `gross`. A gross figure that silently ignores a lockout is the exact
 * defect this module exists to remove.
 */
export function netDeployable(gross: number | null, lock: HarvestLockout): DeployableFigure {
  const g = num(gross);

  if (!lock.known) {
    return {
      gross: g,
      net: null,
      unknown: true,
      suppression: "unknown",
      disclosure:
        g === null
          ? lock.detail
          : `${fmtLockUsd(g)} before any harvest lockout. ${lock.detail}`,
    };
  }

  if (lock.status === "locked_not_applied") {
    return {
      gross: g,
      net: g,
      unknown: g === null,
      suppression: "not_applied",
      disclosure: lock.detail,
    };
  }

  const withheld = lock.withheldFromBuyBudget ?? 0;
  if (withheld <= 0) {
    return {
      gross: g,
      net: g,
      unknown: g === null,
      suppression: "none",
      disclosure: lock.detail,
    };
  }

  if (g === null) {
    return {
      gross: null,
      net: null,
      unknown: true,
      suppression: "unknown",
      disclosure: `Figure unavailable, and ${lock.detail.charAt(0).toLowerCase()}${lock.detail.slice(1)}`,
    };
  }

  const net = Math.max(g - withheld, 0);
  const full = net <= 0;
  return {
    gross: g,
    net,
    unknown: false,
    suppression: full ? "full" : "partial",
    disclosure: full
      ? `${fmtLockUsd(g)} before the lockout; the lockout withholds ${fmtLockUsd(withheld)}, ` +
        `leaving nothing to deploy. ${lock.detail}`
      : // PARTIAL SUPPRESSION — the case that was silent before. Say the amount and the date.
        `Net of the harvest lockout: ${fmtLockUsd(g)} less ${fmtLockUsd(withheld)} withheld ` +
        `until ${fmtUnlockDate(lock.nextUnlockAtUTC)}. ${lock.detail}`,
  };
}

/* =======================================================================================
 * THE OTHER SIDE OF THE SEAM: what `POST /pending_orders/{id}/disposition` says about the
 * lockout, and what the Trade Desk is allowed to print about it.
 *
 * WHY IT IS HERE AND NOT INLINE IN TradingDesk.tsx. The disposition response is the one
 * place the backend reports that a lockout was NOT started, and there are now three
 * different reasons for that, two of which are new (2026-09-04):
 *
 *   already_active     a lockout was already running on this contract. Benign.
 *   full_exit          the disposition closed the whole line, so there is no runner to
 *                      redeploy around and no lockout is CORRECT (P1-FULLEXIT).
 *   lock_write_failed  the trade IS recorded and the lockout is NOT — the capital is not
 *                      withheld and there is no lock for the release control to operate
 *                      on. This is an ERROR, not a variant of success (P1-ATOMICITY).
 *
 * The branch that read this used to fall through to an empty string for anything it did
 * not recognise, so a failed lockout rendered as a plain green "recorded." Keeping the
 * mapping here means it is unit-testable against the REAL handler's bytes
 * (src/lib/__fixtures__/harvestSeam.json) instead of only reachable by rendering a page.
 * ==================================================================================== */

export type DispositionHarvestLockLike = {
  recorded?: unknown;
  reason?: unknown;
  detail?: unknown;
  error?: unknown;
  unlockAtUTC?: unknown;
  /** Backend caveats about the gate itself (pending_order_disposition emits these on
   *  harvestLock.warnings). The load-bearing one says the held book could not be read, so
   *  it could not be confirmed that a runner remains -- the designed mitigation for
   *  starting a lock on silence. Dropping it here would leave that mitigation existing
   *  only inside the backend. */
  warnings?: unknown;
} | null | undefined;

export type DispositionLockNotice = {
  /** "ok" prints inside the success banner; "error" must replace it. */
  tone: "none" | "ok" | "error";
  /** Sentence to show. Empty only when there is nothing to say. */
  text: string;
};

/**
 * What the Trade Desk should say about the lockout after a disposition.
 *
 * The rule that matters: `recorded === true` is the ONLY value that may read as "the clock
 * started". Everything else says, in words, that no lockout exists — including a reason
 * this build has never heard of.
 */
export function describeDispositionHarvestLock(lock: DispositionHarvestLockLike): DispositionLockNotice {
  if (!lock || typeof lock !== "object") return { tone: "none", text: "" };

  const reason = typeof lock.reason === "string" ? lock.reason : "";
  const detail = typeof lock.detail === "string" ? lock.detail.trim() : "";
  const unlock = typeof lock.unlockAtUTC === "string" ? lock.unlockAtUTC.slice(0, 10) : "";
  const unlockNote = unlock ? ` Unlocks ${unlock}.` : "";
  // Carry the backend's own caveats through to the screen, verbatim, on every branch.
  const warnNote = Array.isArray(lock.warnings)
    ? lock.warnings
        .filter((w): w is string => typeof w === "string" && w.trim().length > 0)
        .map((w) => ` ${w.trim()}`)
        .join("")
    : "";

  if (lock.recorded === true) {
    return { tone: "ok", text: `60-day redeployment lockout started.${unlockNote}${warnNote}` };
  }

  if (reason === "already_active") {
    return {
      tone: "ok",
      text: `A redeployment lockout was already running on this contract, so no second one was started.${unlockNote}${warnNote}`,
    };
  }

  // P1-FULLEXIT. Not an error: closing the line entirely is exactly when no lockout is
  // right. Said out loud so the CIO is never left guessing whether a clock started.
  if (reason === "full_exit") {
    return {
      tone: "ok",
      text: `No redeployment lockout: ${
        detail || "this closed the whole line, so there is no runner to redeploy around."
      }${warnNote}`,
    };
  }

  // P1-ATOMICITY, and anything unrecognised. The trade is recorded, the capital is NOT
  // withheld, and no lock exists to release — that can never render as a success.
  const err = typeof lock.error === "string" && lock.error.trim() ? ` (${lock.error.trim()})` : "";
  if (reason === "lock_write_failed") {
    return {
      tone: "error",
      text:
        `the 60-day redeployment lockout was NOT started, so this harvested capital is still counted ` +
        `as deployable. ${detail}${err} Logged to the audit trail as HOME_RUN_LOCK_NOT_STARTED.${warnNote}`,
    };
  }

  return {
    tone: "error",
    text:
      `the 60-day redeployment lockout was NOT started (${reason || "reason not given"}), so this ` +
      `harvested capital is still counted as deployable.${detail ? ` ${detail}` : ""}${err}${warnNote}`,
  };
}
